<script>
import { useVuelidate } from '@vuelidate/core';
import { required, email, sameAs } from '@vuelidate/validators';

/**
 * Trocar o e-mail nao e uma edicao de campo qualquer: e a credencial de login
 * do Aceleres. Errar o endereco tranca a pessoa para fora, e so o gestor
 * devolve. Por isso sai do lapis inline e vira um formulario proprio, com
 * confirmacao do endereco (contra erro de digitacao) e senha (contra tercei-
 * ros num computador destravado).
 */
export default {
  props: {
    currentEmail: {
      type: String,
      default: '',
    },
    saving: {
      type: Boolean,
      default: false,
    },
    // Recusa vinda do servidor (senha errada, endereco em uso). Mantem o
    // dialogo aberto para a pessoa corrigir em vez de perder o que digitou.
    serverError: {
      type: String,
      default: '',
    },
  },
  emits: ['close', 'submit'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      newEmail: '',
      confirmEmail: '',
      password: '',
      mostrarSenha: false,
    };
  },
  validations() {
    return {
      newEmail: { required, email },
      confirmEmail: { required, sameAsEmail: sameAs(this.newEmail) },
      password: { required },
    };
  },
  computed: {
    igualAoAtual() {
      const novo = this.newEmail.trim().toLowerCase();
      return novo !== '' && novo === this.currentEmail.trim().toLowerCase();
    },
    podeSalvar() {
      return !this.v$.$invalid && !this.igualAoAtual && !this.saving;
    },
  },
  mounted() {
    this.$nextTick(() => this.$refs.newEmailInput?.focus());
  },
  methods: {
    submit() {
      this.v$.$touch();
      if (!this.podeSalvar) return;

      this.$emit('submit', {
        email: this.newEmail.trim().toLowerCase(),
        currentPassword: this.password,
      });
    },
  },
};
</script>

<template>
  <Teleport to="body">
    <div class="acl-email-modal__backdrop" @click.self="$emit('close')">
      <section
        class="acl-email-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="acl-email-modal-title"
        @keydown.esc="$emit('close')"
      >
        <header class="acl-email-modal__header">
          <h2 id="acl-email-modal-title">
            {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.TITLE') }}
          </h2>
          <button
            type="button"
            class="acl-email-modal__close"
            :aria-label="$t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.CANCEL')"
            @click="$emit('close')"
          >
            <i class="i-lucide-x" aria-hidden="true" />
          </button>
        </header>

        <form class="acl-email-modal__body" @submit.prevent="submit">
          <p class="acl-email-modal__warning">
            <i class="i-lucide-triangle-alert" aria-hidden="true" />
            <span>{{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.WARNING') }}</span>
          </p>

          <div class="acl-email-modal__field">
            <span class="acl-email-modal__label">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.CURRENT') }}
            </span>
            <p class="acl-email-modal__current">{{ currentEmail }}</p>
          </div>

          <div class="acl-email-modal__field">
            <label class="acl-email-modal__label" for="acl-new-email">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.NEW_LABEL') }}
            </label>
            <input
              id="acl-new-email"
              ref="newEmailInput"
              v-model="newEmail"
              class="acl-email-modal__input"
              type="email"
              autocomplete="off"
              spellcheck="false"
              :placeholder="
                $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.NEW_PLACEHOLDER')
              "
              @input="v$.newEmail.$touch"
            />
            <p v-if="v$.newEmail.$error" class="acl-email-modal__error">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.NEW_ERROR') }}
            </p>
            <p v-else-if="igualAoAtual" class="acl-email-modal__error">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.NEW_SAME_AS_CURRENT') }}
            </p>
          </div>

          <div class="acl-email-modal__field">
            <label class="acl-email-modal__label" for="acl-confirm-email">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.CONFIRM_LABEL') }}
            </label>
            <input
              id="acl-confirm-email"
              v-model="confirmEmail"
              class="acl-email-modal__input"
              type="email"
              autocomplete="off"
              spellcheck="false"
              onpaste="return false"
              :placeholder="
                $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.CONFIRM_PLACEHOLDER')
              "
              @input="v$.confirmEmail.$touch"
            />
            <p v-if="v$.confirmEmail.$error" class="acl-email-modal__error">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.CONFIRM_ERROR') }}
            </p>
          </div>

          <div class="acl-email-modal__field">
            <label class="acl-email-modal__label" for="acl-email-password">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.PASSWORD_LABEL') }}
            </label>
            <div class="acl-email-modal__password">
              <input
                id="acl-email-password"
                v-model="password"
                class="acl-email-modal__input acl-email-modal__input--password"
                :type="mostrarSenha ? 'text' : 'password'"
                autocomplete="current-password"
                :placeholder="
                  $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.PASSWORD_PLACEHOLDER')
                "
                @input="v$.password.$touch"
              />
              <button
                type="button"
                class="acl-email-modal__eye"
                :aria-label="
                  mostrarSenha
                    ? $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.PASSWORD_HIDE')
                    : $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.PASSWORD_SHOW')
                "
                :aria-pressed="mostrarSenha"
                @click="mostrarSenha = !mostrarSenha"
              >
                <i
                  :class="mostrarSenha ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                  aria-hidden="true"
                />
              </button>
            </div>
            <p v-if="v$.password.$error" class="acl-email-modal__error">
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.PASSWORD_ERROR') }}
            </p>
          </div>

          <p v-if="serverError" class="acl-email-modal__server-error">
            {{ serverError }}
          </p>

          <footer class="acl-email-modal__footer">
            <button
              type="button"
              class="acl-email-modal__button"
              @click="$emit('close')"
            >
              {{ $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.CANCEL') }}
            </button>
            <button
              type="submit"
              class="acl-email-modal__button acl-email-modal__button--primary"
              :disabled="!podeSalvar"
            >
              {{
                saving
                  ? $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.SUBMITTING')
                  : $t('PROFILE_SETTINGS.FORM.EMAIL.CHANGE.SUBMIT')
              }}
            </button>
          </footer>
        </form>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.acl-email-modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.38);
}

.acl-email-modal {
  width: min(460px, 100%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
}

.acl-email-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 22px 0;
}

.acl-email-modal__header h2 {
  margin: 0;
  color: #111827;
  font-size: 1.0625rem;
  font-weight: 700;
}

.acl-email-modal__close {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
}

.acl-email-modal__close:hover {
  background: #f3f4f6;
}

.acl-email-modal__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 22px 22px;
}

.acl-email-modal__warning {
  display: flex;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fef6e7;
  color: #92400e;
  font-size: 0.8125rem;
  line-height: 1.45;
}

.acl-email-modal__warning i {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-top: 2px;
}

.acl-email-modal__field {
  display: flex;
  flex-direction: column;
}

.acl-email-modal__label {
  margin-bottom: 6px;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.045em;
  text-transform: uppercase;
}

.acl-email-modal__current {
  margin: 0;
  padding: 9px 12px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 0.9375rem;
}

.acl-email-modal__input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 0.9375rem;
  outline: none;
}

.acl-email-modal__input:focus {
  border-color: #1b8755;
  box-shadow: 0 0 0 3px rgba(27, 135, 85, 0.14);
}

.acl-email-modal__password {
  position: relative;
  display: flex;
}

.acl-email-modal__input--password {
  width: 100%;
  padding-right: 42px;
}

.acl-email-modal__eye {
  position: absolute;
  top: 50%;
  right: 4px;
  display: inline-flex;
  width: 32px;
  height: 32px;
  transform: translateY(-50%);
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
}

.acl-email-modal__eye:hover,
.acl-email-modal__eye:focus-visible {
  background: #f3f4f6;
  color: #1b8755;
  outline: none;
}

.acl-email-modal__eye i {
  width: 17px;
  height: 17px;
}

.acl-email-modal__error {
  margin: 6px 0 0;
  color: #dc2626;
  font-size: 0.75rem;
}

.acl-email-modal__server-error {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 0.8125rem;
  line-height: 1.45;
}

.acl-email-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 4px;
}

.acl-email-modal__button {
  height: 38px;
  padding: 0 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 600;
}

.acl-email-modal__button--primary {
  border-color: #1b8755;
  background: #1b8755;
  color: #ffffff;
}

.acl-email-modal__button--primary:disabled {
  border-color: #d1d5db;
  background: #d1d5db;
  color: #f9fafb;
  cursor: not-allowed;
}

:global(.dark) .acl-email-modal {
  background: #1f2426;
}

:global(.dark) .acl-email-modal__header h2 {
  color: #f3f4f6;
}

:global(.dark) .acl-email-modal__current {
  background: #2a2f35;
  color: #d4d4d8;
}

:global(.dark) .acl-email-modal__input {
  border-color: #3f3f46;
  background: #16191b;
  color: #f3f4f6;
}

:global(.dark) .acl-email-modal__eye:hover,
:global(.dark) .acl-email-modal__eye:focus-visible {
  background: #2a2f35;
}

:global(.dark) .acl-email-modal__button {
  border-color: #3f3f46;
  background: #16191b;
  color: #e4e4e7;
}
</style>
